/**
 * Step-up re-verification, in front of an action that cannot be undone.
 *
 * ProDesk builds no MFA — `core-mfa` challenges at sign-in, before this app is
 * involved (`services/prodesk-service/app/platform/stepup.py`). What this app
 * needs is a **fresh** re-verification for issuing a fee invoice, crediting
 * one, sharing a document, approving a refund and publishing a site
 * (plan §5).
 *
 * ---
 *
 * ## Where the contract actually is
 *
 * `stepup.py` says *"the client obtains an assertion from core-mfa"*, and it is
 * not on core-mfa. `core-mfa` (8102) offers `/totp/*`, `/status`,
 * `/internal/challenge/send` and `/internal/verify` — an internal-key surface a
 * browser cannot call and which mints no assertion. **The step-up surface is on
 * `core-auth`**, and it is two routes:
 *
 *     POST /api/auth/stepup/otp/send   { method }                → sends a code
 *     POST /api/auth/stepup/challenge  { client_id, method, code } → the assertion
 *
 * Both take the ordinary bearer. `client_id` is this app's registered OAuth
 * client — `SSO_CLIENT_ID`, which is the same string
 * `prodesk-service`'s `settings.client_id` checks the assertion's `aud`
 * against, and that audience check is the whole point: an assertion minted for
 * a payroll run must not satisfy ProDesk's gate.
 *
 * Read off the **running** core stack on 2026-09-10 (CLAUDE.md §8 rule 2: read
 * the running system, not the document about it). The platform's own
 * `APP_DEVELOPMENT_GUIDE` does not name these routes and neither did anything
 * in this repo — a documentation gap raised with the next platform PR, not a
 * missing capability.
 *
 * ## What is deliberately tolerant, and why
 *
 * The challenge response's shape is not published — `/openapi.json` types it as
 * `{}` — so `readAssertion` accepts the handful of names an assertion could
 * arrive under, and the envelope with or without a `data` wrapper. That is not
 * defensive coding for its own sake: guessing one name and being wrong would
 * make step-up fail with *"re-verification did not return an assertion"* for
 * every firm, and the alternative was to not ship the button at all.
 *
 * The **methods** are the same situation. `/stepup/otp/send` requires a bearer
 * before it validates its body, so the vocabulary could not be read from the
 * outside; the screen offers the three a person plausibly holds and shows
 * core's own refusal verbatim when one is not available to them.
 */

import { ApiError } from './api'
import { AUTH_API, SSO_CLIENT_ID } from './config'
import { session } from './session'

/** The re-verification methods offered. Core decides which a person actually
 *  holds and says so; this list is what the screen can put on a button. */
/** `sms`, not `mobile`: core-auth's `/stepup/otp/send` takes `email | sms` and refuses anything else
 *  with *"This channel is not available for your account."* — which every "Text me a code" met until
 *  C27 (UAT report #15; `repo:fiveox:services/auth-service/app/api/stepup.py`). */
export const STEPUP_METHODS = ['email', 'sms', 'totp'] as const
export type StepUpMethod = (typeof STEPUP_METHODS)[number]

export const METHOD_LABELS: Record<StepUpMethod, string> = {
  email: 'Email me a code',
  sms: 'Text me a code',
  totp: 'Use my authenticator app',
}

async function post(path: string, body: unknown): Promise<unknown> {
  // `session.authed` owns the bearer, the 401 refresh-and-replay and ending the
  // session when the refresh comes back spent — the same fetcher every other
  // core call in this app goes through.
  const res = await session.authed(`${AUTH_API}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const payload = (await res.json().catch(() => null)) as
    | { success?: boolean; data?: unknown; error?: { code: string; message: string } }
    | null
  if (!res.ok || payload?.success === false) {
    throw new ApiError(
      payload?.error?.code ?? 'INTERNAL',
      payload?.error?.message ?? 'Re-verification could not be started.',
      res.status,
    )
  }
  return payload
}

/** Ask core to send a one-time code. Returns nothing useful; the code goes to
 *  the person, not to us. */
export async function sendCode(method: StepUpMethod): Promise<void> {
  await post('/stepup/otp/send', { method })
}

/**
 * Exchange the code for an assertion.
 *
 * The returned string goes into `X-StepUp-Assertion` on the one request it was
 * minted for. **It is never stored** — not in `localStorage`, not in a module
 * variable, not on the session. A step-up assertion that outlived its action
 * would be a re-verification that re-verified nothing, which is the failure the
 * gate exists to prevent.
 */
export async function assertStepUp(method: StepUpMethod, code: string): Promise<string> {
  const payload = await post('/stepup/challenge', {
    client_id: SSO_CLIENT_ID,
    method,
    code: code.trim(),
  })
  const assertion = readAssertion(payload)
  if (!assertion) {
    throw new ApiError(
      'INTERNAL',
      'Re-verification succeeded but returned no assertion. Please try again.',
      502,
    )
  }
  return assertion
}

/** The assertion, whatever core called it. See the module docstring. */
export function readAssertion(payload: unknown): string | null {
  const roots: unknown[] = [payload]
  if (payload && typeof payload === 'object' && 'data' in payload) {
    roots.push((payload as { data: unknown }).data)
  }
  const names = [
    'assertion',
    'step_up_assertion',
    'stepup_assertion',
    'step_up_token',
    'stepup_token',
    'token',
    'access_token',
  ]
  for (const root of roots) {
    if (!root || typeof root !== 'object') continue
    for (const name of names) {
      const value = (root as Record<string, unknown>)[name]
      // A JWT and nothing else: three dot-separated segments. Without the
      // shape check a `token` field holding a request id would sail through and
      // be refused by the service as an invalid assertion, which points the
      // reader at the wrong end of the exchange.
      if (typeof value === 'string' && value.split('.').length === 3) return value
    }
  }
  return null
}
